import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { useEffect } from "react"
import { Helmet } from "react-helmet-async"
import { FuseguLogo } from "@/components/FuseguLogo"
import { ArrowLeft, CheckCircle, XCircle, DollarSign, Zap, Users } from "lucide-react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

export default function WordfenceAlternative() {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <>
      <Helmet>
        <title>Wordfence Alternative - Multi-Site Security for Agencies | Fusegu</title>
        <meta
          name="description"
          content="Looking for a Wordfence alternative for agencies? Fusegu offers comprehensive security scanning for unlimited sites, white-label reports, and multi-platform support. Try free."
        />
        <meta property="og:title" content="Wordfence Alternative - Fast, Comprehensive Security for All Platforms" />
        <meta property="og:description" content="Switch from Wordfence to Fusegu for multi-site security scanning beyond WordPress. Scan any web application with professional white-labeled reports." />
        <meta property="og:url" content="https://fusegusecurity.com/wordfence-alternative" />
        <meta name="keywords" content="Wordfence alternative, WordPress security, multi-site security scanner, website security, vulnerability scanning" />
      </Helmet>
      <div className="flex min-h-screen w-full flex-col">
        {/* Header */}
        <header className="sticky top-0 flex h-16 items-center gap-4 border-b bg-background px-4 md:px-6 z-50">
          <div className="flex items-center justify-between w-full">
            <Link to="/" className="flex items-center gap-2">
              <FuseguLogo className="h-6 w-6 flex-shrink-0" />
              <span className="text-lg font-bold">Fusegu</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button>Start Free Trial</Button>
              </Link>
            </div>
          </div>
        </header>

        <main className="flex-1 py-12 px-4 md:px-8 bg-gradient-to-br from-slate-50 to-blue-50">
          <div className="max-w-4xl mx-auto">
            {/* Back to home link */}
            <Link to="/" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6">
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Home</span>
            </Link>

            {/* Article Header */}
            <div className="mb-8">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                Wordfence Alternative - Multi-Platform Security for Agencies
              </h1>
              <p className="text-xl text-gray-700 leading-relaxed">
                Looking for a Wordfence alternative? Fusegu offers comprehensive security scanning for
                WordPress AND any other web application - perfect for agencies managing diverse client sites.
              </p>
            </div>

            {/* Quick Value Props */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <Card className="border-2 border-blue-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Zap className="h-6 w-6 text-blue-600" />
                    <CardTitle className="text-lg">Any Platform</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-700 mb-2">Scan WordPress, React, Node.js, PHP, and more</p>
                  <p className="text-sm font-semibold text-blue-600">Not just WordPress</p>
                </CardContent>
              </Card>

              <Card className="border-2 border-green-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Users className="h-6 w-6 text-green-600" />
                    <CardTitle className="text-lg">Unlimited Sites</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-700 mb-2">50 scans per day</p>
                  <p className="text-sm font-semibold text-green-600">One flat rate</p>
                </CardContent>
              </Card>

              <Card className="border-2 border-purple-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-6 w-6 text-purple-600" />
                    <CardTitle className="text-lg">White-Label</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-700 mb-2">Professional client reports</p>
                  <p className="text-sm font-semibold text-purple-600">Included in base plan</p>
                </CardContent>
              </Card>
            </div>

            {/* Why Teams Switch */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-2xl">Why Teams Switch from Wordfence to Fusegu</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">Scan Beyond WordPress</p>
                    <p className="text-gray-600">Wordfence only works for WordPress. Fusegu scans any web application - React, Node.js, PHP, custom apps, and more.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">No Plugin Installation Required</p>
                    <p className="text-gray-600">Fusegu scans externally without installing plugins on every client site. Save hours of setup time.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">Centralized Dashboard for All Sites</p>
                    <p className="text-gray-600">Manage security for all client sites from one dashboard instead of logging into each WordPress admin panel.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">White-Label Client Reports</p>
                    <p className="text-gray-600">Generate professional PDF reports with your agency branding. Wordfence doesn't offer this feature.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">Broader Security Testing</p>
                    <p className="text-gray-600">Get OWASP ZAP, SSL Labs, Nuclei, Nmap, and more - not just WordPress-specific checks.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Comparison Table */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-2xl">Wordfence vs Fusegu</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[200px]">Feature</TableHead>
                        <TableHead>Wordfence</TableHead>
                        <TableHead>Fusegu</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-medium">Platform Support</TableCell>
                        <TableCell>WordPress only</TableCell>
                        <TableCell className="font-bold text-green-600">Any web application</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Price (Premium)</TableCell>
                        <TableCell>$149/year per site</TableCell>
                        <TableCell className="font-bold text-green-600">$49/month for unlimited sites</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Installation</TableCell>
                        <TableCell>Plugin on each site</TableCell>
                        <TableCell className="font-bold text-green-600">No installation needed</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Centralized Dashboard</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                            <span className="text-sm">Wordfence Central (separate)</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="font-bold">Built-in</span>
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">White-label Reports</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="font-bold">Included</span>
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Security Scanners</TableCell>
                        <TableCell>Wordfence proprietary</TableCell>
                        <TableCell className="font-bold">10+ tools (OWASP ZAP, Nuclei, SSL Labs, etc.)</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">SSL/TLS Scanning</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="font-bold">Yes (SSL Labs)</span>
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">SOC2 Compliance Mapping</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="font-bold">Yes</span>
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Real-time Firewall</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="font-bold">Yes</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                            <span>Scanning only</span>
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Malware Removal</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="font-bold">Yes (Premium+)</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                            <span>Detection only</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* When Wordfence is Better */}
            <Card className="mb-8 border-2 border-orange-200">
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <span>When Wordfence is Better</span>
                  <Badge variant="outline" className="ml-2">Honest Comparison</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-700">Wordfence is excellent for:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Real-time WordPress firewall protection</strong> - Blocks attacks before they reach your site</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Active malware removal</strong> - Premium+ plan includes cleaning infected sites</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <span><strong>WordPress-only agencies</strong> - If you exclusively manage WordPress sites</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Continuous protection</strong> - 24/7 monitoring and blocking of threats</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* When Fusegu is Better */}
            <Card className="mb-8 border-2 border-green-200">
              <CardHeader>
                <CardTitle className="text-2xl text-green-600">When Fusegu is Better</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-700">Fusegu is perfect for:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Multi-platform agencies</strong> - Manage WordPress, React, Node.js, PHP, and custom apps from one dashboard</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Agencies with many client sites</strong> - Unlimited scanning at one flat rate vs $119/year per site</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Professional client reporting</strong> - White-labeled PDFs with your branding included</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Comprehensive security audits</strong> - OWASP Top 10, SSL/TLS, infrastructure, and more</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>External scanning</strong> - No plugin installation needed, scan any publicly accessible site</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Compliance requirements</strong> - SOC2 compliance mapping and security documentation</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Pricing Comparison */}
            <Card className="mb-8 border-2 border-blue-200">
              <CardHeader>
                <CardTitle className="text-2xl">Pricing Comparison for Agencies</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <p className="text-lg font-semibold mb-2">Annual Cost for 20 Client Sites:</p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-100 rounded-lg">
                        <p className="text-sm text-gray-600">Wordfence Premium</p>
                        <p className="text-3xl font-bold">$2,980/year</p>
                        <p className="text-sm text-gray-600">20 sites × $149/year</p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg border-2 border-green-300">
                        <p className="text-sm text-green-700">Fusegu</p>
                        <p className="text-3xl font-bold text-green-600">$588/year</p>
                        <p className="text-sm text-green-700">Unlimited sites</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-2 border-green-200">
                    <p className="text-2xl font-bold text-green-700 mb-2">You Save: $2,392/year</p>
                    <p className="text-gray-700">And with Fusegu you can scan unlimited sites, not just 20. Plus you get white-label reports included.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* FAQ Section */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-2xl">Frequently Asked Questions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <p className="font-semibold text-lg mb-2">Can Fusegu replace Wordfence for WordPress security?</p>
                  <p className="text-gray-700">
                    Fusegu excels at comprehensive security scanning and vulnerability detection. However, Wordfence
                    offers real-time firewall protection and active malware removal. Many agencies use Fusegu for
                    regular security audits and client reporting, while keeping Wordfence free version for basic
                    firewall protection on critical sites.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-lg mb-2">Do I need to install anything on my client sites?</p>
                  <p className="text-gray-700">
                    No! Fusegu scans externally without requiring any plugin installation. Just enter the URL and
                    start scanning. This saves hours compared to installing Wordfence on every client site.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-lg mb-2">Can Fusegu scan non-WordPress sites?</p>
                  <p className="text-gray-700">
                    Yes! This is a major advantage over Wordfence. Fusegu can scan React apps, Node.js applications,
                    PHP sites, custom web applications, and more. Perfect for agencies with diverse client portfolios.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-lg mb-2">How do the white-label reports work?</p>
                  <p className="text-gray-700">
                    Upload your agency logo and customize colors in the Branding section. All PDF reports will
                    automatically include your branding. Show clients professional security reports without any
                    "Fusegu" branding.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-lg mb-2">What security issues does Fusegu detect?</p>
                  <p className="text-gray-700">
                    Fusegu runs 10+ industry-standard scanners including OWASP ZAP, SSL Labs, Nuclei, and Nmap.
                    We detect OWASP Top 10 vulnerabilities, SSL/TLS issues, outdated software, security headers,
                    WordPress-specific problems, and infrastructure vulnerabilities.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-lg mb-2">Can I try Fusegu alongside Wordfence?</p>
                  <p className="text-gray-700">
                    Absolutely! Many agencies run both in parallel. Use Fusegu for comprehensive security audits
                    and client reporting, and keep Wordfence for real-time protection. Start with a free trial
                    - no credit card required.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* CTA Section */}
            <Card className="mb-8 bg-gradient-to-br from-blue-600 to-purple-700 text-white border-none">
              <CardContent className="p-8 text-center">
                <h2 className="text-3xl font-bold mb-4">Try Fusegu Free - No Credit Card Required</h2>
                <p className="text-xl mb-6 text-blue-50">
                  Scan your first site in 5 minutes. See how Fusegu complements or replaces Wordfence.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/register">
                    <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 font-semibold px-8">
                      Start Free Trial
                    </Button>
                  </Link>
                  <Link to="/">
                    <Button size="lg" className="bg-blue-700 text-white hover:bg-blue-800 font-semibold px-8 border-2 border-white">
                      View Demo
                    </Button>
                  </Link>
                </div>
                <p className="text-sm mt-4 text-blue-100">
                  Join hundreds of agencies scanning beyond WordPress
                </p>
              </CardContent>
            </Card>

          </div>
        </main>

        {/* Footer */}
        <footer className="border-t py-8 px-4 md:px-8 bg-white">
          <div className="max-w-4xl mx-auto text-center text-sm text-gray-600">
            <p className="mb-4">Fusegu Security | fusegusecurity.com</p>
            <div className="flex justify-center gap-6 flex-wrap">
              <Link to="/privacy" className="hover:text-blue-600">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-blue-600">Terms of Service</Link>
              <Link to="/blogs" className="hover:text-blue-600">Blog</Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  )
}
